import { createTheme } from '@mui/material/styles';

import { config } from '../libs/config';

/**
 * The console's dark theme.
 *
 * Colours only. The layout, the widgets and what any screen does are unchanged - this sets the
 * palette they are drawn in, taken from the TwinWorX dashboard design.
 *
 * Applies to the admin shell and everything inside it. The login and sign-out screens are
 * separate routes outside Layout's ThemeProvider, so they never see this and stay as they were.
 *
 * Lives in its own module rather than inside Layout(), and must stay that way. A theme built
 * during render produces a new object each time, which defeats MUI's memoisation and - because
 * the styled AppBar and Drawer are derived from it - remounts the drawer subtree, closing any
 * expanded menu section. Layout consumes a context that updates on every MQTT message, so that
 * used to happen whenever an alarm arrived.
 */

/*
 * Three planes, not two.
 *
 * The shell - navigation rail and top bar - sits below the page, the page sits below the cards
 * on it. That ordering is what makes a screen readable: the frame recedes and the content comes
 * forward. Putting the shell at the card colour instead, which is what this did first, left a
 * screen like Messages as one continuous slab from the menu through the title bar into the
 * grid, with nothing to tell them apart.
 */
const CHROME = '#080d16';
const PAGE = '#0b111c';
const SURFACE = '#141e2e';

// Hairline edges and separators. White at low alpha rather than a fixed grey, so it sits
// correctly over both surfaces above without being tuned twice.
const HAIRLINE = 'rgba(255, 255, 255, 0.09)';

const darkTheme = createTheme({
    palette: {
        mode: 'dark',

        // Brighter than the #447296 this replaced. That was chosen against a white page and
        // carries too little contrast on a dark one, and it also tints every DataGrid hover and
        // selection through CustomDataGrid's alpha() calls, where it disappeared entirely.
        primary: {
            main: '#3b82f6',
            light: '#60a5fa',
            dark: '#2563eb'
        },

        // The status set from the design. These also back the alarm viewer's tiles and the
        // service health indicators, which were already close to these hues.
        success: { main: '#4ade80' },
        // #ef4444, the design's red, measures 4.45 against the card surface - just under
        // the 4.5 needed to be legible as text, and serviceHealth uses it as text.
        error: { main: '#f16565' },
        warning: { main: '#f59e0b' },
        info: { main: '#38bdf8' },

        background: {
            default: PAGE,
            paper: SURFACE
        },

        /*
         * Four levels, and primary is deliberately the second of them.
         *
         * MUI resolves almost everything to text.primary, so whatever that is becomes the colour
         * of the whole application - menu items, titles, grid cells and field values alike. Set
         * to the brightest value, as it was, nothing can step up from it and every screen reads
         * as one flat sheet of white regardless of what any element means.
         *
         * So the default is the body level, and the things that should lead - page and card
         * titles, the value half of a label-and-value pair, the menu item you are on - name
         * `bright` and step up. Measured on the card surface: 15.4, 11.1, 5.9 and 3.7.
         */
        text: {
            bright: '#f2f6fc',
            primary: '#c8d3e2',
            secondary: '#8b9bb4',
            disabled: '#66788f'
        },

        divider: HAIRLINE,

        /*
         * Section and card headings - "Message Volume (Today)", "Alarm Destinations", and a
         * dozen more that name a group of fields.
         *
         * They asked for the accent, which read as a heading when the accent was the muted
         * #447296 against white. Against this page the accent is a brighter blue and the same
         * headings read as links. A token of their own rather than the accent, so each theme
         * can answer the question separately: bright here, and the colour they always were on
         * light.
         */
        heading: '#f2f6fc'
    },

    components: {
        /*
         * MUI's dark mode lays a lighter gradient over Paper as elevation rises, so a card
         * inside a card inside a dialog drifts progressively paler. The design has flat
         * surfaces at one colour, separated by a border, so the overlay is turned off and the
         * border supplied instead.
         */
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none'
                },
                outlined: {
                    borderColor: HAIRLINE
                }
            }
        },

        /*
         * The shell's top bar. Set explicitly because an AppBar's dark-mode colour depends on
         * enableColorOnDark, which is the kind of default that changes between versions - and
         * this one carries boxShadow: none already, so without a border it met the page with no
         * edge at all.
         */
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: CHROME,
                    backgroundImage: 'none',
                    borderBottom: `1px solid ${HAIRLINE}`,
                    // Stated, so anything on the bar can inherit it. What sits here has to take
                    // its colour from the bar rather than from the page's text scale: the bar is
                    // its own surface, and in light mode it is a coloured one whose readable
                    // text is white - naming the page's brightest level there painted black on
                    // #447296.
                    color: '#f2f6fc'
                }
            }
        },

        // The navigation rail, on the same plane as the top bar so the two read as one frame.
        // Below the page rather than above it, which is what stops the menu, the title bar and
        // the grid on a screen like Messages running together as a single slab.
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: CHROME,
                    backgroundImage: 'none',
                    borderRight: `1px solid ${HAIRLINE}`
                }
            }
        },

        // Dialogs sit above a scrim on an already dark page. Left at the surface colour with a
        // border, so the edge is visible where the scrim alone would not distinguish it.
        MuiDialog: {
            styleOverrides: {
                paper: {
                    backgroundImage: 'none',
                    border: `1px solid ${HAIRLINE}`
                }
            }
        },

        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: HAIRLINE
                }
            }
        },

        /*
         * Menu items, which were the flattest thing on the screen: every one of them the same
         * weight and the same colour, with nothing marking the one you are on. Colour alone was
         * never going to fix that - there was no state to hang a colour on, so the menu is now
         * route-aware and this is what being on a page looks like.
         *
         * Unselected items sit at the secondary level so the menu reads as navigation rather
         * than as content competing with the page.
         */
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    color: '#8b9bb4',
                    // Reserved on every item, coloured only on the selected one, so selecting an
                    // item does not shift the label sideways by the width of the marker.
                    borderLeft: '3px solid transparent',

                    '&.Mui-selected': {
                        color: '#f2f6fc',
                        borderLeftColor: '#3b82f6',
                        backgroundColor: 'rgba(59, 130, 246, 0.12)',

                        '&:hover': {
                            backgroundColor: 'rgba(59, 130, 246, 0.18)'
                        },

                        // The icon leads with the label rather than staying grey behind it.
                        '& .MuiListItemIcon-root': {
                            color: '#3b82f6'
                        }
                    }
                }
            }
        },

        // Icons follow their label's level rather than being separately dark, which on a rail
        // this dark left them reading as disabled.
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    color: 'inherit'
                }
            }
        },

        /*
         * Section headings in the rail. Dropped to the dim level and set in small letterspaced
         * capitals, which is how the design labels a group - the size and spacing carry it, so
         * the colour does not have to compete with the items underneath.
         *
         * The background matters: a ListSubheader defaults to the paper colour, which on a rail
         * at the chrome plane would have drawn a lighter band across it.
         */
        MuiListSubheader: {
            styleOverrides: {
                root: {
                    backgroundColor: 'transparent',
                    color: '#66788f',
                    textTransform: 'uppercase',
                    fontSize: '0.6875rem',
                    letterSpacing: '0.09em',
                    fontWeight: 600
                }
            }
        },

        /*
         * Column headings, distinguished from the data under them. They inherited the same
         * colour as the cells, so a grid was an even field of text with its headings indicated
         * only by a rule beneath them.
         */
        MuiTableCell: {
            styleOverrides: {
                head: {
                    color: '#8b9bb4',
                    fontWeight: 600,
                    letterSpacing: '0.03em'
                }
            }
        }
    },

    /*
     * Weight does work that colour cannot, and it survives the two levels being close together.
     * The design sets large text light and small labels medium - the opposite of the default,
     * where a heading is bold and everything else is regular - so a title leads by size and air
     * rather than by thickness.
     */
    typography: {
        h5: { fontWeight: 300 },
        h6: { fontWeight: 400 },
        subtitle1: { fontWeight: 400 },
        subtitle2: { fontWeight: 500, letterSpacing: '0.02em' },
        overline: { letterSpacing: '0.09em', fontWeight: 600 }
    }
});

/**
 * The light theme: what this console looked like before the dark palette, kept available rather
 * than designed.
 *
 * Deliberately not a light counterpart of the dark theme. It is the single primary against
 * MUI's default light mode that the console had, so a site that sets REACT_APP_THEME=light gets
 * the arrangement it already knows rather than a second palette nobody has reviewed. None of
 * the dark theme's component overrides apply here, which is the point: they exist to build a
 * hierarchy against a near-black page and would be wrong against a white one.
 *
 * text.bright is defined, and that matters. Seventeen page titles name it, and a colour named
 * in sx that the palette does not carry is not an error - MUI finds nothing at that path, the
 * browser discards the string, and the element inherits its parent. Titles would still render,
 * by accident rather than by decision. Set to MUI's own light primary, so they come out exactly
 * as they did before any of this.
 */
const lightTheme = createTheme({
    palette: {
        mode: 'light',
        primary: { main: '#447296' },
        text: { bright: 'rgba(0, 0, 0, 0.87)' },
        // The colour these headings have always been on white - they asked for the accent, and
        // on this theme the accent is still it.
        heading: '#447296'
    }
});

/**
 * Colours for the JSON and XML views in the message and log dialogs.
 *
 * Here because react-json-formatter takes its styling as props and ships no themes, so these
 * have to be supplied - and they were supplied five times over, as literals, in applog, logs
 * and messages. Two of those literals were `blue` and `green`, which on the dark page are close
 * to unreadable.
 *
 * One copy per mode, so a palette change cannot reach four of the five and leave the fifth
 * behind. Per mode rather than shared because this is the one place where carrying the wrong
 * set over does not merely look off: bright-on-dark code colours measure about 1.6 against
 * white, and the light set's blue and green are what prompted the dark set in the first place.
 */
const darkJsonViewStyle = {
    propertyStyle: { color: '#7ee787' },
    stringStyle: { color: '#79c0ff' },
    numberStyle: { color: '#ffa657' },
    booleanStyle: { color: '#d2a8ff' },
    nullStyle: { color: '#8b9bb4' }
};

/** The same palette for the XML view, which is hand-rendered rather than formatter-driven. */
const darkXmlViewStyle = {
    bracket: { color: '#8b9bb4' },
    tag: { color: '#7ee787' },
    attrName: { color: '#ffa657' },
    attrValue: { color: '#79c0ff' },
    text: { color: '#e6edf7' }
};

/*
 * The light set is the console's original: the CSS keywords it used before the dark palette,
 * which are dark enough to read on white and were only a problem against a near-black page.
 */
const lightJsonViewStyle = {
    // The same colours, written as hex rather than as CSS keywords. Identical output - green is
    // #008000, blue is #0000ff - but a keyword cannot be measured, and these are the values the
    // contrast test has to be able to read.
    propertyStyle: { color: '#008000' },
    stringStyle: { color: '#0000ff' },
    numberStyle: { color: '#ff0000' },
    booleanStyle: { color: '#6f42c1' },
    nullStyle: { color: '#808080' }
};

const lightXmlViewStyle = {
    bracket: { color: '#808080' },
    tag: { color: '#008000' },
    attrName: { color: '#b22222' },
    attrValue: { color: '#0000ff' },
    text: { color: '#333333' }
};

/*
 * Which of the two this deployment draws.
 *
 * Resolved once, here, from configuration read at module load - so the theme stays a module
 * constant. Chosen per render it would defeat MUI's memoisation and, because the styled AppBar
 * and Drawer derive from it, remount the drawer subtree and close any expanded menu section.
 *
 * Dark unless a site asks otherwise. An unrecognised value is a typo rather than a third
 * theme, and falls back the same way alarmRouting does: a console drawn in the default is
 * better than one that will not draw.
 */
const isLight = config.theme === 'light';

export const appTheme = isLight ? lightTheme : darkTheme;
export const jsonViewStyle = isLight ? lightJsonViewStyle : darkJsonViewStyle;
export const xmlViewStyle = isLight ? lightXmlViewStyle : darkXmlViewStyle;

/* Named exports for the tests, which check both regardless of how this deployment is set. */
export { darkTheme, lightTheme, darkJsonViewStyle, lightJsonViewStyle,
         darkXmlViewStyle, lightXmlViewStyle };
